import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { contractTitlesApi } from "../../api/contractTitleApi";
import type { ContractTitle } from "../../types/contractTitle.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";

interface ContractTitlesState {
  items: ContractTitle[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: ContractTitlesState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const contractTitlesSlice = createSlice({
  name: "contractTitles",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setContractTitles: (state, action: PayloadAction<ContractTitle[]>) => {
      state.items = action.payload;
    },
    appendContractTitles: (state, action: PayloadAction<ContractTitle[]>) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleContractTitleLocally: (
      state,
      action: PayloadAction<ContractTitle>,
    ) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetContractTitleList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleContractTitle: (
      state,
      action: PayloadAction<Partial<ContractTitle> & { id: string }>,
    ) => {
      const index = state.items.findIndex((ct) => ct.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleContractTitle: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((ct) => ct.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setContractTitles,
  appendContractTitles,
  addSingleContractTitleLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetContractTitleList,
  updateSingleContractTitle,
  removeSingleContractTitle,
} = contractTitlesSlice.actions;

interface FetchContractTitlesPayload {
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  sortAscending?: boolean;
  groupIds?: string[];
  ids?: string[];
  searchText?: string;
  includeDeleted?: boolean;
  /** Dropdowns pass true so deactivated entries can't be picked;
   *  the listing filter passes null for "show both". */
  isActive?: boolean | null;
}

export const fetchContractTitles = (
  payload: FetchContractTitlesPayload = {},
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().contractTitles;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        // This endpoint reads the flag as "ascending" despite its name — a
        // quirk every dropdown in the app relies on, so it is matched here
        // rather than changed there.
        sortDecending: payload.sortAscending ?? false,
        groupIds: payload.groupIds || [userData?.companyId],
        ids: payload.ids,
        searchText: payload.searchText || "",
        includeDeleted: payload.includeDeleted || false,
        ...(payload.isActive !== undefined && { isActive: payload.isActive }),
      };

      const response =
        await contractTitlesApi.searchContractTitles(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setContractTitles(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendContractTitles(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Contract Titles error:", error);
      toast.error(
        i18n.t("common:slices.contractTitles.fetchError", {
          defaultValue: "Failed to fetch contract titles",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addContractTitle = (data: { contractTitle: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));

      const payload = {
        contractTitle: data.contractTitle,
      };

      const res = await contractTitlesApi.createContractTitle(payload);

      const createdId = res?.data?.id || res?.data || res?.id || res;
      const newContractTitle: ContractTitle = {
        contractTitle: data.contractTitle,
        id: typeof createdId === "string" ? createdId : createdId?.id,
        groupId: res?.data?.groupId || res?.groupId || "",
      };

      dispatch(addSingleContractTitleLocally(newContractTitle));

      toast.success(
        i18n.t("contractTitle:messages.addSuccess", {
          defaultValue: "Contract Title created successfully",
        }),
      );
      return newContractTitle;
    } catch (error) {
      console.error("Create Contract Title error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("contractTitle:messages.addError", {
          defaultValue: "Failed to create contract title",
        })));
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editContractTitle = (data: {
  id: string;
  contractTitle: string;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));

      const payload = {
        id: data.id,
        contractTitle: data.contractTitle,
      };

      const res = await contractTitlesApi.updateContractTitle(payload);
      const updatedTitle = res?.data || payload;

      dispatch(updateSingleContractTitle(updatedTitle));

      toast.success(
        i18n.t("contractTitle:messages.editSuccess", {
          defaultValue: "Contract Title updated successfully",
        }),
      );
      return updatedTitle;
    } catch (error) {
      console.error("Edit Contract Title error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("contractTitle:messages.editError", {
          defaultValue: "Failed to update contract title",
        })));
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const removeContractTitle = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleContractTitle(id));

      await contractTitlesApi.removeContractTitle(id);

      toast.success(
        i18n.t("contractTitle:messages.removeSuccess", {
          defaultValue: "Contract Title removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Contract Title error:", error);
      // The API refuses deletion while contracts or staff still use the
      // record and says which — show that reason, not a generic failure.
      const fallback = i18n.t("contractTitle:messages.removeError", {
        defaultValue: "Failed to remove contract title",
      });
      reportDeleteFailure(error, fallback);
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default contractTitlesSlice.reducer;

export const activateContractTitle = (id: string) => {
  return async (dispatch: AppDispatch) => {
    // Optimistic, then rolled back on failure — same shape as the
    // department/business-unit toggles.
    dispatch(updateSingleContractTitle({ id, isActive: true } as any));

    try {
      await contractTitlesApi.activateContractTitle(id);

      toast.success(
        i18n.t("common:slices.contractTitles.activateSuccess", {
          defaultValue: "Contract Title activated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleContractTitle({ id, isActive: false } as any));
      console.error("Activate Contract Title error:", error);
      toast.error(
        i18n.t("common:slices.contractTitles.activateError", {
          defaultValue: "Failed to activate contract title",
        }),
      );
    }
  };
};

export const deactivateContractTitle = (id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(updateSingleContractTitle({ id, isActive: false } as any));

    try {
      await contractTitlesApi.deactivateContractTitle(id);

      toast.success(
        i18n.t("common:slices.contractTitles.deactivateSuccess", {
          defaultValue: "Contract Title deactivated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleContractTitle({ id, isActive: true } as any));
      console.error("Deactivate Contract Title error:", error);
      toast.error(
        i18n.t("common:slices.contractTitles.deactivateError", {
          defaultValue: "Failed to deactivate contract title",
        }),
      );
    }
  };
};
